<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { animate } from "motion-v";
import { reactive, useTemplateRef } from "vue";
import * as z from "zod";
import EmailIcon from "#/assets/iconsDynamic/Email.vue";
import { useModal } from "#/composables/useModal";
import { useValidation } from "#/composables/useValidation";

const emit = defineEmits<(e: "confirm", orderId: string, providerType: string, email: string) => void>();

const { t } = useTranslation();
const inputEl = useTemplateRef("inputEl");
const schema = z.object({ email: z.email(t("cart-required-email.enter-correct-email")).min(1, t("cart-required-email.enter-email")) });
const form = reactive<z.infer<typeof schema>>({ email: "" });

const { validate, issues } = useValidation(form, schema);
const { visible, context, close } = useModal<{ orderId: string; providerType: string }>("cart-required-email");

const onSubmit = async () => {
	const isValid = await validate();
	if (!isValid) {
		if (inputEl.value) animate(inputEl.value, { x: [0, -10, 0, 10, 0] }, { duration: 0.1, repeat: 2 });
		return;
	}

	emit("confirm", context.value.orderId, context.value.providerType, form.email);
	close();
};
</script>

<template>
	<Transition name="fade">
		<div v-if="visible" class="cart_required_email_modal_overlay" @click.self="close">
			<form class="cart_required_email_modal_content" @submit.prevent="onSubmit">
				<div class="cart_required_email_container">
					<div class="icon">
						<EmailIcon />
					</div>
					<div class="content">
						<div class="title">{{ t('cart-required-email.title') }}</div>
						<div class="subtitle">{{ t('cart-required-email.subtitle') }}</div>
					</div>
				</div>

				<input ref="inputEl" type="email" v-model="form.email" placeholder="you@example.com" :aria-invalid="!!issues.email">
				<button type="submit">{{ t('cart-required-email.button.continue') }}</button>

				<p class="cart_required_email_modal_footer">{{ t('cart-required-email.footer') }}</p>
			</form>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
  @use './cartRequiredEmail.scss';
</style>
